/**
 * Ring numbers — the primary human identity of a bird.
 *
 * A closed ring is stamped with a federation code, the breeder's own code, the year and
 * a sequence number, e.g. `NL-1234-25-007`. Federations differ in exactly what they
 * stamp, which is why the authoritative format check is a per-federation pattern held as
 * seed data and validated server-side. What lives here is parsing and formatting for
 * display: splitting a ring number so the year can be shown separately, and normalising
 * what a breeder types on a phone.
 */

export interface ParsedRingNumber {
  federationCode: string | null;
  breederCode: string | null;
  /** Four-digit year, resolved from the two digits stamped on the ring. */
  year: number | null;
  sequence: string | null;
  /** The value as stored, uppercased and trimmed. */
  normalized: string;
}

/** Matches the common `FED-BREEDER-YY-SEQ` shape. Anything else is kept verbatim. */
const RING_PATTERN = /^([A-Z]{1,4})-([A-Z0-9]{1,8})-(\d{2})-(\d{1,6})$/;

/**
 * Normalises what a breeder typed: trims, uppercases, and collapses the separators
 * people actually use — a phone keyboard makes spaces and slashes far too easy to hit.
 */
export function normalizeRingNumber(input: string): string {
  return input.trim().toUpperCase().replace(/[\s/.]+/g, "-").replace(/-{2,}/g, "-");
}

/**
 * Splits a ring number into its parts, or returns nulls for the parts it cannot read.
 * Never throws: an unparseable ring number is still a valid identifier for a bird whose
 * federation stamps a different format.
 */
export function parseRingNumber(input: string, pivotYear = 70): ParsedRingNumber {
  const normalized = normalizeRingNumber(input);
  const match = RING_PATTERN.exec(normalized);

  if (!match) {
    return {
      federationCode: null,
      breederCode: null,
      year: null,
      sequence: null,
      normalized,
    };
  }

  const [, federationCode, breederCode, twoDigitYear, sequence] = match;

  return {
    federationCode: federationCode ?? null,
    breederCode: breederCode ?? null,
    year: twoDigitYear ? expandYear(Number(twoDigitYear), pivotYear) : null,
    sequence: sequence ?? null,
    normalized,
  };
}

/**
 * Turns the two digits on the ring into a full year. Rings stamped `25` mean 2025, and
 * a breeder with old stock may still hold `98` from 1998 — the pivot decides where the
 * century flips, defaulting to 70 so anything from 70 upwards reads as 1900s.
 */
export function expandYear(twoDigitYear: number, pivotYear = 70): number {
  return twoDigitYear >= pivotYear ? 1900 + twoDigitYear : 2000 + twoDigitYear;
}

/**
 * The short form for a dense list, where the federation and breeder codes are the same
 * on every row and only the year and sequence tell birds apart.
 */
export function shortRingNumber(input: string): string {
  const { year, sequence, normalized } = parseRingNumber(input);

  return year !== null && sequence !== null ? `${String(year).slice(-2)}-${sequence}` : normalized;
}

/** True when two ring numbers refer to the same ring despite being typed differently. */
export function ringNumbersMatch(left: string, right: string): boolean {
  return normalizeRingNumber(left) === normalizeRingNumber(right);
}
